import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Button, Props as ButtonProps } from 'app/components/Common/Button'
import { Close } from 'app/components/Common/Close'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { styles, theme } from 'app/theme'
import { Media } from 'app/theme/media'
import useLockedBody from 'app/utils/useLockedBody'
import { useVocabularyData } from 'app/utils/vocabulary'
import { rgba } from 'emotion-rgba'
import { Link } from 'gatsby'
import React, { memo, useState } from 'react'
import { useInView } from 'react-intersection-observer'

export interface Props {
  cta?: ButtonProps
  description?: string
  file?: string
  image?: ImageProps
  label?: string
  languageCode: string
  showModal?: boolean
  title?: string
  URL?: string
}

export const FlashNews = memo(function FlashNews({
  cta,
  description,
  file,
  image,
  label,
  languageCode,
  showModal,
  title,
  URL,
}: Props) {
  if (!label) {
    return null
  }

  const [animObjectRef, isAnimObjectVisible] = useInView({
    triggerOnce: true,
  }) as any

  const [_locked, setLocked] = useLockedBody()
  const [modalFlashNewsStatus, setModalFlashNewsStatus] = useState(false)
  const [modalForceShow, setModalForceShow] = useState(showModal)
  const buttonDOM = (
    <LabelContent>
      <LabelTitle className="label-title">
        {useVocabularyData('news', languageCode)}
      </LabelTitle>
      <Inner>{label}</Inner>
    </LabelContent>
  )
  const EXTERNAL_URL_REGEX = new RegExp('^(https?|ftp|www)://')
  const externalURL = URL && EXTERNAL_URL_REGEX.test(URL)

  return (
    <>
      {label ? (
        URL ? (
          externalURL ? (
            <ExtLink
              className={isAnimObjectVisible ? 'visible' : undefined}
              ref={animObjectRef}
              href={URL}
              rel="noopener"
              target="_blank"
            >
              {buttonDOM}
            </ExtLink>
          ) : (
            <Anchor
              className={isAnimObjectVisible ? 'visible' : undefined}
              ref={animObjectRef}
              to={URL}
            >
              {buttonDOM}
            </Anchor>
          )
        ) : file ? (
          <ExtLink
            className={isAnimObjectVisible ? 'visible' : undefined}
            href={file}
            ref={animObjectRef}
            rel="noopener"
            target="_blank"
          >
            {buttonDOM}
          </ExtLink>
        ) : (
          <Label
            className={isAnimObjectVisible ? 'visible' : undefined}
            ref={animObjectRef}
            onClick={() => {
              if (!modalFlashNewsStatus) {
                setLocked(true)
                setModalFlashNewsStatus(true)
              } else {
                setLocked(false)
                setModalFlashNewsStatus(false)
              }
            }}
          >
            {buttonDOM}
          </Label>
        )
      ) : null}

      <Modal
        className={modalFlashNewsStatus || modalForceShow ? 'open' : undefined}
        dial={5}
        onClick={() => {
          setLocked(false)
          setModalFlashNewsStatus(false)
          setModalForceShow(false)
        }}
      >
        <Wrapper row stretch onClick={(e) => e.stopPropagation()}>
          <Media lessThan="ipadVertical">
            <Close
              className="modal-close"
              variant="flash-news"
              onClick={() => {
                setLocked(false)
                setModalFlashNewsStatus(false)
                setModalForceShow(false)
              }}
            />
          </Media>
          {image && (modalFlashNewsStatus || modalForceShow) ? (
            <ImageWrap>
              <Image {...image} />
            </ImageWrap>
          ) : null}

          <TextWrap className={!image ? 'full-width' : undefined}>
            {title ? <Title>{title}</Title> : null}

            {description ? (
              <Description dangerouslySetInnerHTML={{ __html: description }} />
            ) : null}

            {cta ? <CTA variant="white" {...cta} /> : null}
            <Media greaterThanOrEqual="ipadVertical">
              <Close
                className="modal-close"
                variant="flash-news"
                onClick={() => {
                  setLocked(false)
                  setModalFlashNewsStatus(false)
                  setModalForceShow(false)
                }}
              />
            </Media>
          </TextWrap>
        </Wrapper>
      </Modal>
    </>
  )
})

const Style = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 11.625rem;
  height: 11.625rem;
  border-radius: 50%;
  cursor: pointer;
  overflow: hidden;
  position: absolute;
  top: 10.4375rem;
  right: 10vw;
  text-align: center;
  transform: scale(0);

  div {
    color: ${theme.colors.variants.neutralLight3};
  }

  &:before {
    content: '';
    width: 100%;
    height: 100%;
    background-color: ${rgba(theme.colors.variants.neutralLight3, 0.2)};
    backdrop-filter: blur(1.875rem) brightness(0.9);
    border-radius: 50%;
    position: absolute;
    top: 0;
    left: 0;
    transform: scale(0.971);
    transition: 0.4s ease-in-out;
  }
  &:after {
    content: '';
    width: 10.375rem;
    height: 10.375rem;
    border: 0.0625rem solid ${theme.colors.variants.neutralLight3};
    background: ${rgba(theme.colors.variants.primaryDark, 0)};
    border-radius: 50%;
    opacity: 1;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    transition: 0.4s ease-in-out;
  }
  @media (min-width: 1200px) {
    &:hover {
      .label-title {
        color: ${theme.colors.variants.primaryDark};
      }
      &:before {
        transform: scale(1);
      }
      &:after {
        width: 10.375rem;
        height: 10.375rem;
        border: 0.0625rem solid ${theme.colors.variants.neutralLight1};
        background: ${rgba(theme.colors.variants.neutralLight1, 1)};
      }

      div {
        color: ${theme.colors.variants.neutralDark1};
      }
    }
  }
  &.visible {
    transform: scale(1);
    transition-delay: 1s;
    transition-duration: 0.8s;
  }

  @supports not ((-webkit-backdrop-filter: none) or (backdrop-filter: none)) {
    background-color: ${rgba(theme.colors.variants.neutralLight3, 0.8)};
  }

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    background-color: ${rgba(theme.colors.variants.neutralLight3, 0.8)};
  }

  @media (max-width: 1199px) {
    background: transparent;
    backdrop-filter: blur(0.25rem) brightness(0.8);
    display: block;
    width: calc(100% - 3rem);
    height: auto;
    border-radius: 0;
    top: 6rem;
    left: 1.5rem;
    right: 1.5rem;
    transform: none;
    &:before {
      display: none;
    }
    &:after {
      display: none;
    }
    &.visible {
      transform: none;
      transition-delay: 0s;
      transition-duration: 0s;
    }
  }
`

const Label = styled.div`
  ${Style}
`

const Anchor = styled(Link)`
  ${Style}
`

const ExtLink = styled.a`
  ${Style}
`

const LabelContent = styled.div`
  position: relative;
  z-index: 2;
  transition: 0.4s ease-in-out;

  @media (max-width: 1199px) {
    text-align: left;
    padding: 0.75rem 2rem 0.75rem 0.75rem;
    z-index: 7;

    &:after {
      content: '';
      display: block;
      position: absolute;
      top: 50%;
      right: 0.75rem;
      width: 0;
      height: 0;
      border-style: solid;
      border-width: 0 0.1875rem 0.25rem 0.1875rem;
      border-color: transparent transparent #fff transparent;
      transform: rotate(90deg);
    }
  }
`

const LabelTitle = styled.div`
  ${styles.label}

  transition: 0.4s ease-in-out;
  margin: 0.375rem 0;

  @media (max-width: 1199px) {
    margin: 0 auto 0.25rem;
  }
`

const Inner = styled.div`
  ${styles.description.light}

  transition: 0.4s ease-in-out;
  font-size: 0.75rem;
  line-height: 1rem;
  max-width: 8.125rem;
  margin: auto;

  @media (max-width: 1199px) {
    max-width: none;
    line-height: 1rem;
  }
`

const Modal = styled(FlexBox)`
  width: 100%;
  height: 100%;
  cursor: pointer;
  opacity: 0;
  padding: 0 1.5rem;
  position: fixed;
  top: 0;
  left: 0;
  visibility: hidden;
  z-index: -1;
  transition: 0.6s cubic-bezier(0.25, 1, 0.5, 1);

  &:before {
    content: '';
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.colors.variants.neutralDark3};
    opacity: 0.4;
    position: absolute;
    top: 0;
    left: 0;
  }

  &.open {
    opacity: 1;
    visibility: visible;
    z-index: 9999;

    > div {
      opacity: 1;
      transition: 0.6s cubic-bezier(0.25, 1, 0.5, 1);
    }
  }

  @media (max-width: 767px) {
    padding: 0 1.5rem;
  }
`

const Wrapper = styled(FlexBox)`
  position: relative;
  width: 100%;
  max-width: 56.25rem;
  aspect-ratio: 1.72;
  background: ${({ theme }) => theme.colors.variants.neutralLight3};
  cursor: auto;
  margin: auto;
  overflow: hidden;
  opacity: 0;

  @media (max-width: 991px) {
    aspect-ratio: 1.5;
  }

  @media (max-width: 767px) {
    display: block;
    height: 80vh;
    padding: 0;
    max-height: -webkit-fill-available;
    overflow-y: auto;
    transform: none;
    -webkit-overflow-scrolling: auto;

    .modal-close {
      background: ${({ theme }) => theme.colors.variants.neutralLight2};
      top: 0;
      right: 0;
      width: 3rem;
      height: 3rem;
    }
  }
`

const ImageWrap = styled.div`
  width: 50%;
  overflow: hidden;
  position: relative;

  img {
    ${styles.img}

    transform: scale(1.05);
  }

  @media (max-width: 1023px) {
    width: 100%;
  }

  @media (max-width: 767px) {
    padding-bottom: 60%;
  }
`

const TextWrap = styled.div`
  width: 50%;
  padding: 5rem 4.167vw;
  position: relative;
  &.full-width {
    width: 100%;
    text-align: center;
  }

  @media (max-width: 1199px) {
    width: 100%;
    padding: 3rem;
  }
  @media (max-width: 767px) {
    padding: 2.25rem 1.5rem;
  }
`

const Title = styled.h2`
  ${styles.title.normal}
`

const Description = styled.div`
  ${styles.description.dark}

  display: -webkit-box;
  margin-top: 1rem;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 10;
  -webkit-box-orient: vertical;

  @media (max-width: 1199px) {
    display: block;
    overflow: auto;
  }
`

const CTA = styled(Button)`
  margin-top: 1.5rem;
`
